import {
  Box,
  Tab,
  Card,
  Chip,
  Table,
  Stack,
  Button,
  Avatar,
  Dialog,
  Tooltip,
  Divider,
  TableRow,
  Collapse,
  TableHead,
  TableCell,
  TableBody,
  CardHeader,
  Typography,
  IconButton,
  CardContent,
  CardActions,
  DialogTitle,
  ListItemText,
  DialogContent,
} from '@mui/material';
import Grid from '@mui/material/Unstable_Grid2';
import { useTranslation } from 'react-i18next';
import * as Sentry from '@sentry/react';
import { useMemo, useState, Fragment, useEffect } from 'react';
import axios from '@/utils/api/axios';
import { TabList, TabPanel, TabContext, LoadingButton } from '@mui/lab';
import FormProvider from '@/components/forms/hook-form';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import {
  OrderTypes,
  ISalesOrder,
  IMilestoneItem,
  IOrderDocument,
  IOrganizationItem,
  ChoicesOrderStatus,
  ChoicesOrderItemLabel,
} from '@/types';
import { ShowIf } from '@/utils';
import { useSnackbar } from 'notistack';
import { RHFNumberText } from '@/components/forms/hook-form/rhf-number-field';
import InfoIcon from '@mui/icons-material/Info';
import Iconify from '@/components/media/iconify';
import { ExpandMore, ExpandLess } from '@mui/icons-material';
import AttachFileIcon from '@mui/icons-material/AttachFile';
import Label from '@/components/core/label';
import { CopyText } from '@/components/utils/copy-text';
import { OrganizationInfoCard } from '@/components/data-display/organization';
import moment from 'moment';
import { OrderDocumentsSection } from '@/sections/management/accounts/orders/forms/components/order-documents-section';

import {
  ChoicesOrderItemType,
  ChoicesOrderUnitMeasure,
} from '@/sections/management/accounts/orders/types';

type Props = {
  currentOrder?: any;
  currentExistsOrder?: any;
  onBack: () => void;
  onReset: () => void;
  onCancel: () => void;
};

const useBillingOrderData = (orderId: string, milestones: string[]) => {
  const { data: requestData } = useQuery({
    queryKey: ['request', orderId],
    queryFn: async () => {
      try {
        const response = await axios.get(`/requests/${orderId}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching request data:', error);
        return null;
      }
    },
    enabled: !!orderId,
  });

  const { data: milestonesData } = useQuery({
    queryKey: ['milestones', milestones],
    queryFn: async () => {
      if (!milestones || milestones.length === 0) return [];

      try {
        const promises = milestones.map(async (milestoneId) => {
          try {
            const response = await axios.get(`/milestones/${milestoneId}`);
            return response.data;
          } catch (error) {
            console.error(`❌ Error fetching milestone ${milestoneId}:`, error);
            return null;
          }
        });

        const results = await Promise.all(promises);
        const validResults = results.filter(Boolean);
        return validResults;
      } catch (error) {
        console.error('❌ Error fetching milestones data:', error);
        return [];
      }
    },
    enabled: !!milestones && milestones.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutos
    cacheTime: 10 * 60 * 1000, // 10 minutos
  });

  const { data: salesOrdersData } = useQuery({
    queryKey: ['salesOrders', orderId],
    queryFn: async () => {
      try {
        const response = await axios.get(`/orders/${orderId}/sales-orders`);
        return response.data;
      } catch (error) {
        console.error('❌ Error fetching salesOrders data:', error);
        return null;
      }
    },
    enabled: !!orderId,
    staleTime: 5 * 60 * 1000, // 5 minutos
    cacheTime: 10 * 60 * 1000, // 10 minutos
  });

  return {
    requestData,
    milestonesData,
    salesOrdersData,
    isLoading: false,
  };
};
//TODO: Remove this component and their children after the QA Validations
export function BillingOrderNewEditThirdStepForm({
  currentOrder,
  currentExistsOrder,
  onBack,
  onReset,
  onCancel,
}: Props) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const { enqueueSnackbar } = useSnackbar();
  const [organizationData, setOrganizationData] = useState<IOrganizationItem | null>(null);
  const [completeOrder, setCompleteOrder] = useState<boolean>(false);
  const validatorOrderCreated =
    currentExistsOrder && currentExistsOrder?.status !== ChoicesOrderStatus.created;

  const { requestData, milestonesData, salesOrdersData } = useBillingOrderData(
    currentExistsOrder?._id || '',
    currentExistsOrder?.milestones || []
  );

  const { data: requestDataOld } = useQuery({
    queryKey: ['request', currentExistsOrder?.requests?.[0]],
    queryFn: async () => {
      if (!currentExistsOrder?.requests?.[0]) return null;

      try {
        const response = await axios.get(`/requests/${currentExistsOrder.requests[0]}`);
        return response.data;
      } catch (error) {
        console.error('Error fetching request data:', error);
        return null;
      }
    },
    enabled: !!currentExistsOrder?.requests?.[0],
  });

  const itemsCurrentOrder = useMemo(() => {
    const milestonesToUse =
      currentOrder?.order?.selectedMilestones ||
      currentOrder?.order?.milestones ||
      currentExistsOrder?.milestones ||
      [];

    // If they are IDs (editing), try to get data from the backend
    if (typeof milestonesToUse[0] === 'string') {
      // 1. Try to use milestonesData from personalized hook
      if (milestonesData && milestonesData.length > 0) {
        const items = milestonesData.map((milestone: any, index: number) => {
          const item = {
            _id: milestone.data?._id || `milestone-${index}`,
            product: {
              name:
                milestone.data?.product?.name || `Producto ${milestone.data?.number || index + 1}`,
              label:
                milestone.data?.product?.name || `Producto ${milestone.data?.number || index + 1}`,
            },
            sku: {
              name: milestone.data?.product?.sku || `SKU-${milestone.data?.number || index + 1}`,
              label: milestone.data?.product?.sku || `SKU-${milestone.data?.number || index + 1}`,
            },
            plan: {
              name: milestone.data?.product?.plan || `Plan ${milestone.data?.number || index + 1}`,
              label: milestone.data?.product?.plan || `Plan ${milestone.data?.number || index + 1}`,
              code: milestone.data?.product?.plan || `PLAN-${milestone.data?.number || index + 1}`,
            },
            type: milestone.data?.type || 'subscription',
            unitAmount: milestone.data?.unitAmount || 1,
            totalAmount: milestone.data?.totalAmount || 0,
            quantity: milestone.data?.unitAmount || milestone.data?.quantity || 1,
            finalCustomMilestones: [milestone.data?._id],
            orderItemType: 'newResource',
            partner: milestone.data?.orderItem?.partner || milestone.data?.product?.partner,
            summary: {
              provision: {
                provisionDate: new Date(),
                endDate: new Date(),
                quantity: milestone.data?.unitAmount || milestone.data?.quantity || 1,
                milestones: [milestone.data?._id],
              },
            },
          };

          return item;
        });

        return items;
      }

      // 2. Try to use Salesordersdata of the personalized hook
      if (salesOrdersData && salesOrdersData.length > 0) {
        const items = salesOrdersData.map((salesOrder: any, index: number) => {
          const item = {
            _id: salesOrder.data?._id || `sales-order-${index}`,
            product: {
              name:
                salesOrder.data?.product?.name ||
                `Producto ${salesOrder.data?.number || index + 1}`,
              sku: salesOrder.data?.product?.sku || `SKU-${salesOrder.data?.number || index + 1}`,
              plan:
                salesOrder.data?.product?.plan || `Plan ${salesOrder.data?.number || index + 1}`,
              planName:
                salesOrder.data?.product?.planName ||
                `PLAN-${salesOrder.data?.number || index + 1}`,
            },
            type: salesOrder.data?.type || 'subscription',
            unitAmount: salesOrder.data?.unitAmount || 1,
            totalAmount: salesOrder.data?.totalAmount || 0,
            finalCustomMilestones: [milestonesToUse[index] || `milestone-${index}`],
          };

          return item;
        });

        return items;
      }

      // 3. Try to use personalized hook request
      if (requestData?.data?.items && requestData.data.items.length > 0) {
        const items = requestData.data.items.map((item: any, index: number) => ({
          _id: item._id || `request-item-${index}`,
          orderItem: {
            _id: item._id || `request-item-${index}`,
            product: item.product || {
              name: `Producto ${index + 1}`,
              label: `Producto ${index + 1}`,
            },
            sku: item.sku || { name: `SKU-${index + 1}`, label: `SKU-${index + 1}` },
            plan: item.plan || {
              name: `Plan ${index + 1}`,
              label: `Plan ${index + 1}`,
              code: `PLAN-${index + 1}`,
            },
            quantity: item.quantity || 1,
            orderItemType: item.orderItemType || 'newResource',
          },
          product: item.product || {
            name: `Producto ${index + 1}`,
            label: `Producto ${index + 1}`,
          },
          sku: item.sku || { name: `SKU-${index + 1}`, label: `SKU-${index + 1}` },
          plan: item.plan || {
            name: `Plan ${index + 1}`,
            label: `Plan ${index + 1}`,
            code: `PLAN-${index + 1}`,
          },
          quantity: item.quantity || 1,
          total: item.total || 0,
          summary: {
            provision: {
              provisionDate: item.provisionDate || new Date(),
              endDate: item.endDate || new Date(),
              quantity: item.quantity || 1,
              milestones: [milestonesToUse[index] || `milestone-${index}`],
            },
          },
          allMilestones: [milestonesToUse[index] || `milestone-${index}`],
          finalCustomMilestones: [milestonesToUse[index] || `milestone-${index}`],
        }));

        return items;
      }

      const defaultItems = milestonesToUse.map((milestoneId: string, index: number) => ({
        _id: `default-item-${index}`,
        orderItem: {
          _id: `default-item-${index}`,
          product: { name: `Producto ${index + 1}`, label: `Producto ${index + 1}` },
          sku: { name: `SKU-${index + 1}`, label: `SKU-${index + 1}` },
          plan: {
            name: `Plan ${index + 1}`,
            label: `Plan ${index + 1}`,
            code: `PLAN-${index + 1}`,
          },
          quantity: 1,
          orderItemType: 'newResource',
        },
        product: { name: `Producto ${index + 1}`, label: `Producto ${index + 1}` },
        sku: { name: `SKU-${index + 1}`, label: `SKU-${index + 1}` },
        plan: { name: `Plan ${index + 1}`, label: `Plan ${index + 1}`, code: `PLAN-${index + 1}` },
        quantity: 1,
        total: 0,
        summary: {
          provision: {
            provisionDate: new Date(),
            endDate: new Date(),
            quantity: 1,
            milestones: [milestoneId],
          },
        },
        allMilestones: [milestoneId],
        finalCustomMilestones: [milestoneId],
      }));

      return defaultItems;
    }

    // If not IDs, use existing data (creation mode)
    // 1) Consume enriched response from the billing endpoint (grouped if available from Step 1)
    const enrichedDocs = (currentOrder?.order?.enrichedBillingDocs || []) as any[];
    if (Array.isArray(enrichedDocs) && enrichedDocs.length > 0) {
      // Map both grouped mode and per-milestone mode (when product/sku/plan are inside orderItem)
      const mapped = enrichedDocs.map((doc: any) => {
        const orderItem = doc.orderItem || {};
        const product = doc.product || orderItem.product || {};
        const sku = doc.sku || orderItem.sku || {};
        const plan = doc.plan || orderItem.plan || {};
        const partner = orderItem.partner || {};
        const subscription = doc.subscription || orderItem.subscription;

        const milestonesArray =
          Array.isArray(doc.milestones) && doc.milestones.length > 0
            ? doc.milestones
            : [
                {
                  id: doc._id,
                  paymentDate: doc.paymentDate,
                  status: doc.status,
                  total: doc.total ?? doc.subtotal ?? 0,
                  orderItem: {
                    id: orderItem._id || orderItem.id,
                    subscription: subscription,
                    quantity: orderItem.quantity,
                    orderItemType: orderItem.orderItemType,
                  },
                },
              ];

        const firstMilestone = milestonesArray[0] || {};
        const totalAmount = (doc.aggregates?.totalAmount ?? firstMilestone.total ?? 0) as number;
        const totalQuantity = (doc.aggregates?.totalQuantity ?? orderItem.quantity ?? 0) as number;
        const quantity =
          doc.summary?.provision?.quantity ?? orderItem.quantity ?? totalQuantity ?? 0;

        const summary = doc.summary?.provision
          ? { provision: doc.summary.provision }
          : {
              provision: {
                provisionDate: doc.startPeriod || doc.paymentDate,
                endDate: doc.endPeriod || doc.paymentDate,
                quantity,
                milestones: milestonesArray.map((m: any) => m.id || m._id).filter(Boolean),
              },
            };

        return {
          _id: orderItem._id || orderItem.id || subscription || firstMilestone.id || doc._id,
          orderItem: {
            ...orderItem,
            partner,
          },
          product,
          sku,
          plan,
          subscription,
          orderItemType: orderItem.orderItemType,
          quantity,
          total: totalAmount,
          totalQuantity,
          summary,
          allMilestones: milestonesArray,
          finalCustomMilestones: milestonesArray.map((m: any) => m.id || m._id).filter(Boolean),
        } as any;
      });

      const selected =
        currentOrder?.order?.selectedMilestones || currentOrder?.order?.milestones || [];
      const selectedIds = Array.isArray(selected)
        ? selected.map((m: any) => (typeof m === 'string' ? m : m.id || m._id)).filter(Boolean)
        : [];

      const filtered =
        selectedIds.length === 0
          ? mapped
          : mapped.filter((item: any) => {
              const milestoneIds = (item?.allMilestones || [])
                .map((m: any) => m?.id || m?._id)
                .filter(Boolean);
              return milestoneIds.some((id: string) => selectedIds.includes(id));
            });

      return filtered.sort((a, b) => String(a._id).localeCompare(String(b._id)));
    }

    // 2) Fallback: locally group selected milestones (as before)
    const selected =
      currentOrder?.order?.selectedMilestones || currentOrder?.order?.milestones || [];
    if (!Array.isArray(selected) || selected.length === 0) return [];

    type MilestoneDoc = {
      _id?: string;
      id?: string;
      total?: number;
      paymentDate?: unknown;
      status?: string;
      orderItem?: {
        _id?: string;
        id?: string;
        subscription?: string;
        quantity?: number;
        orderItemType?: string;
        sku?: { name?: string };
        plan?: { name?: string };
      };
    };

    type OrderItemDoc = {
      _id: string;
      product?: unknown;
      subscription?: string;
      plan?: unknown;
      quantity?: number;
    };

    interface MilestoneGroup {
      milestones: MilestoneDoc[];
      orderItem: OrderItemDoc;
    }

    const milestonesBySubscription = selected.reduce(
      (acc: Record<string, MilestoneGroup>, milestone: any) => {
        const subscriptionId = milestone.orderItem?.subscription || milestone?.subscription;
        if (!subscriptionId) return acc;
        if (!acc[subscriptionId]) {
          acc[subscriptionId] = { milestones: [], orderItem: milestone.orderItem };
        }
        acc[subscriptionId].milestones.push(milestone);
        return acc;
      },
      {}
    );

    const uniqueItems = Object.values(milestonesBySubscription).map((group: MilestoneGroup) => {
      const { milestones, orderItem } = group;
      const { totalAmount, totalQuantity } = milestones.reduce(
        (acc: { totalAmount: number; totalQuantity: number }, m: any) => ({
          totalAmount: acc.totalAmount + (m.total || 0),
          totalQuantity: acc.totalQuantity + (m.orderItem?.quantity || 0),
        }),
        { totalAmount: 0, totalQuantity: 0 }
      );

      return {
        _id: orderItem._id,
        orderItem,
        product: orderItem.product,
        subscription: orderItem.subscription,
        plan: orderItem.plan,
        quantity: orderItem.quantity,
        totalQuantity,
        milestone: { ...milestones[0], total: totalAmount },
        allMilestones: milestones,
      } as any;
    });

    return uniqueItems.sort((a, b) => a._id.localeCompare(b._id));
  }, [currentOrder, currentExistsOrder, milestonesData, salesOrdersData, requestData]);

  const dataSalesOrders = useMemo(() => {
    // In create mode, use currentOrder.salesOrders (which comes from step 2)
    if (currentOrder?.salesOrders) {
      return currentOrder.salesOrders;
    }

    // In edit mode, use currentOrder.order.salesOrders
    if (currentOrder?.order?.salesOrders) {
      const normalized = (currentOrder.order.salesOrders as any[]).map((so: any) => {
        const items = Array.isArray(so.items) ? so.items : [];
        const soItems = Array.isArray(so.salesOrdersItems) ? so.salesOrdersItems : [];
        const missingLinks = soItems.some((it: any) => !it?.itemId && !it?.orderItem);

        if (!missingLinks) return so;

        const sourceItems = items.length > 0 ? items : itemsCurrentOrder;
        const rebuiltSalesItems = sourceItems.map((it: any) => ({
          _id: `${so._id}-${it._id}`,
          salesOrder: so._id,
          unitMeasure: 'quantity',
          value: it?.summary?.provision?.quantity ?? it?.totalQuantity ?? it?.quantity ?? 1,
          amount: it?.total ?? it?.totalAmount ?? 0,
          itemId: it?._id,
          subscription: it?.subscription || it?.orderItem?.subscription || '',
          milestones: (it?.allMilestones || []).map((m: any) => m?._id || m?.id).filter(Boolean),
          product: it?.product,
          sku: it?.sku,
          plan: it?.plan,
          orderItemType: it?.orderItemType,
          partner: it?.partner || it?.orderItem?.partner,
        }));

        return {
          ...so,
          items: sourceItems,
          salesOrdersItems: rebuiltSalesItems,
        };
      });

      return normalized;
    }

    // Fallback: to use currentExistsOrder.salesOrders if available
    if (currentExistsOrder?.salesOrders) {
      return currentExistsOrder.salesOrders;
    }

    // If there are no sales orders anywhere, create one by default based on items
    if (itemsCurrentOrder.length > 0) {
      if (salesOrdersData && salesOrdersData.length > 0) {
        return salesOrdersData;
      }

      const defaultSalesOrder = {
        _id: 'default-sales-order',
        legalEntity: currentExistsOrder?.organization || {
          _id: 'default-org',
          name: 'Organización',
        },
        order: currentExistsOrder?._id || 'default-order',
        items: itemsCurrentOrder,
        salesOrdersItems: itemsCurrentOrder.map((item: any, index: number) => ({
          _id: `default-sales-item-${index}`,
          salesOrder: 'default-sales-order',
          unitMeasure: 'quantity',
          value: item.quantity || 1,
          amount: item.total || 0,
          product: item.product,
          sku: item.sku,
          plan: item.plan,
          summary: item.summary,
          quantity: item.quantity,
          total: item.total,
          partner: item?.partner || item?.orderItem?.partner,
        })),
      };

      return [defaultSalesOrder];
    }

    return [];
  }, [currentOrder, currentExistsOrder, itemsCurrentOrder, salesOrdersData]);

  // Obtain request from the request if available

  const itemsById = useMemo(() => {
    const result = itemsCurrentOrder.reduce((acc: any, item: any) => {
      acc[item._id] = item;
      return acc;
    }, {});
    console.log('[BillingOrder][Step3] itemsById:', result);
    return result;
  }, [itemsCurrentOrder]);

  const [value, setValue] = useState<string>('0');
  const methods = useForm();
  const {
    handleSubmit,
    reset,
    formState: { isSubmitting },
  } = methods;
  const { mutateAsync, isLoading } = useMutation({
    mutationFn: async () => {
      const src = currentOrder?.order ?? {};

      // Extract organization ID (may be an object or string)
      const organizationId =
        typeof src.organization === 'string' ? src.organization : src.organization?._id;

      // Extract milestones IDs from selectedMilestones or milestones
      const milestonesSource = src.selectedMilestones || src.milestones || [];
      const milestones = milestonesSource.map((m: any) =>
        typeof m === 'string' ? m : m._id
      );

      // Extract legalEntity as string ID
      const legalEntity =
        typeof src.legalEntity === 'string' ? src.legalEntity : src.legalEntity?._id;

      // Extract submittedBy as string ID
      const submittedBy =
        typeof src.submittedBy === 'string' ? src.submittedBy : src.submittedBy?._id;

      // Build DTO-safe order object — only fields present in CreateOrderDto
      const order: Record<string, any> = {
        orderType: OrderTypes.BILLING_ORDER,
        organization: organizationId,
        milestones,
        items: src.items ?? [],
        itemsToAddLicenses: src.itemsToAddLicenses ?? [],
        itemsToRenewal: src.itemsToRenewal ?? [],
        itemsToUpgrade: src.itemsToUpgrade ?? [],
        contacts: src.contacts ?? [],
        isMarket: src.isMarket ?? false,
        acceptedTermsAndConditions: src.acceptedTermsAndConditions ?? false,
      };

      // Optional fields — only include if present
      if (legalEntity) order.legalEntity = legalEntity;
      if (submittedBy) order.submittedBy = submittedBy;
      if (src.status != null) order.status = src.status;
      if (src.submittedAt != null) order.submittedAt = src.submittedAt;
      if (src.opportunity != null) order.opportunity = src.opportunity;
      if (src.onlySave != null) order.onlySave = src.onlySave;

      // Discounts from either order-level or top-level
      if (src.discounts?.length > 0) {
        order.discounts = src.discounts;
      } else if (currentOrder?.discounts?.length > 0) {
        order.discounts = currentOrder.discounts;
      }

      if (completeOrder) {
        order.onlySave = false;
        order.status = ChoicesOrderStatus.completed;
      }

      // Build salesOrders — use existing or construct from dataSalesOrders
      let salesOrdersPayload = currentOrder?.salesOrders;

      if (!salesOrdersPayload) {
        const salesOrdersSource = Array.isArray(dataSalesOrders) ? dataSalesOrders : [];

        // Try to fetch legal entities of the organization to enrich distribution
        let orgLegalEntities: any[] = [];
        try {
          if (organizationId) {
            const resp = await axios.get(`legal-entities/all`, {
              params: { organization: organizationId },
            });
            orgLegalEntities = Array.isArray(resp?.data?.data) ? resp.data.data : [];
          }
        } catch (e) {
          // ignore, we'll fallback to whatever we have
        }

        const getDefaultLegalEntity = () => {
          if (salesOrdersSource?.[0]?.legalEntity) return salesOrdersSource[0].legalEntity;
          if (orgLegalEntities.length === 1) return orgLegalEntities[0];
          return null;
        };

        const itemByIdMap: Record<string, any> = itemsCurrentOrder.reduce(
          (acc: Record<string, any>, it: any) => {
            acc[String(it._id)] = it;
            return acc;
          },
          {}
        );

        salesOrdersPayload = (
          salesOrdersSource.length > 0
            ? salesOrdersSource
            : [
                {
                  _id: 'auto-legal-entity',
                  legalEntity: getDefaultLegalEntity(),
                  salesOrdersItems: itemsCurrentOrder.map((it: any, index: number) => ({
                    _id: `auto-sales-item-${index}`,
                    unitMeasure: 'quantity',
                    value:
                      it?.summary?.provision?.quantity ?? it?.totalQuantity ?? it?.quantity ?? 1,
                    amount: it?.total ?? it?.totalAmount ?? 0,
                    itemId: it?._id,
                    subscription: it?.subscription || it?.orderItem?.subscription || '',
                    milestones: (it?.allMilestones || [])
                      .map((m: any) => m?._id || m?.id)
                      .filter(Boolean),
                    product: it?.product,
                    sku: it?.sku,
                    plan: it?.plan,
                    orderItemType: it?.orderItemType,
                    partner: it?.orderItem?.partner,
                  })),
                },
              ]
        ).map((so: any) => {
          const le = so.legalEntity || getDefaultLegalEntity() || {};
          const itemsArray = (so.salesOrdersItems || so.items || []).map((it: any) => {
            const refId = String(it?.itemId || it?.orderItem || it?._id || '');
            const ref = itemByIdMap[refId] || {};
            return {
              value: it?.value ?? it?.quantity ?? it?.summary?.provision?.quantity ?? 0,
              amount: it?.amount ?? it?.total ?? 0,
              itemId: it?.itemId || it?.orderItem || it?._id,
              subscription:
                it?.subscription || it?.orderItem?.subscription || ref?.subscription || '',
              milestones:
                it?.milestones ||
                (Array.isArray(it?.allMilestones)
                  ? it.allMilestones.map((m: any) => m?._id || m?.id).filter(Boolean)
                  : (ref?.allMilestones || []).map((m: any) => m?._id || m?.id).filter(Boolean)),
              unitMeasure: it?.unitMeasure || 'quantity',
              product: it?.product || ref?.product,
              sku: it?.sku || ref?.sku,
              plan: it?.plan || ref?.plan,
              orderItemType: it?.orderItemType || ref?.orderItemType,
              partner: it?.partner || ref?.orderItem?.partner,
            };
          });

          return {
            _id: so._id,
            label: so.label || so.legal_name || le.legal_name,
            legal_name: so.legal_name || le.legal_name,
            tax_number: so.tax_number || le.tax_number,
            documents: so.documents || [],
            items: itemsArray,
          };
        });
      }

      const payload: Record<string, any> = { order };
      if (salesOrdersPayload?.length > 0) {
        payload.salesOrders = salesOrdersPayload;
      }

      if (currentExistsOrder) {
        return axios.patch(`orders/${currentExistsOrder?._id}`, payload);
      }
      return axios.post(`orders`, payload);
    },
    onSuccess: async ({ data }) => {
      enqueueSnackbar(t('Order sent!'));
      reset();
      onCancel();

      await queryClient.invalidateQueries({
        queryKey: ['billing-order', data?._id?.toString(), 'edit'],
      });

      const keysSet = new Set([
        'billing-orders',
        'billing-order',
        'organization',
        'organizations',
        'requestList',
        'request',
      ]);

      const currentOrgId =
        currentExistsOrder?.organization?._id ?? currentExistsOrder?.organization;

      await queryClient.invalidateQueries({
        predicate: (q) => {
          const key = q.queryKey as unknown as any[];
          const head = key?.[0];

          if (keysSet.has(head)) return true;

          // Case orderList with parameters
          if (typeof head === 'string' && head.startsWith('orderList') && key.length > 1)
            return true;

          // Case exact billing-orders
          if (
            currentOrgId &&
            head === 'billing-orders' &&
            key[1] === currentOrgId &&
            key.length === 2
          ) {
            return true;
          }

          return false;
        },
      });
    },
    onError: (e) => {
      Sentry.captureException(e, {
        tags: {
          component: 'BillingOrderNewEditThirdStepForm',
          action: currentExistsOrder ? 'update' : 'create',
        },
        extra: {
          orderId: currentExistsOrder?._id,
          orderData: currentOrder,
        },
      });
      enqueueSnackbar(t('Error sending order'), { variant: 'error' });
    },
  });
  const onSubmit = handleSubmit(async () => {
    try {
      await mutateAsync();
    } catch (e) {
      // Error handled by mutation onError
    }
  });
  useEffect(() => {
    const orgId = currentOrder?.order?.organization?._id || currentExistsOrder?.organization?._id;

    if (orgId) {
      axios
        .get(`organizations/${orgId}`, { params: { populate: true } })
        .then((response) => {
          setOrganizationData(response.data.data);
        })
        .catch((error) => {
          enqueueSnackbar(t('Error fetching organization data'), { variant: 'error' });
        });
    }
  }, [currentExistsOrder, currentOrder, enqueueSnackbar, t]);

  const handleChange = (event: React.SyntheticEvent, newValue: string) => {
    setValue(newValue);
  };

  const [expandedRows, setExpandedRows] = useState<number[]>([]);
  const [showDocumentsDialog, setShowDocumentsDialog] = useState(false);
  const [selectedLegalEntityDocuments, setSelectedLegalEntityDocuments] = useState<
    IOrderDocument[]
  >([]);
  const [selectedLegalEntityName, setSelectedLegalEntityName] = useState('');

  // Query to obtain milestones details in edition
  const { data: milestonesDetails } = useQuery({
    queryKey: ['milestones-details', currentOrder?.order?.milestones],
    queryFn: async () => {
      if (
        !currentOrder?.order?.milestones ||
        typeof currentOrder.order.milestones[0] !== 'string'
      ) {
        return [];
      }

      try {
        const promises = currentOrder.order.milestones.map((milestoneId: string) =>
          axios.get(`milestones/${milestoneId}`)
        );
        const responses = await Promise.all(promises);
        return responses.map((response) => response.data.data);
      } catch (error) {
        console.error('Error fetching milestones details:', error);
        return [];
      }
    },
    enabled:
      !!currentOrder?.order?.milestones && typeof currentOrder.order.milestones[0] === 'string',
  });

  const toggleRow = (index: number) => {
    setExpandedRows((prev) =>
      prev.includes(index) ? prev.filter((row) => row !== index) : [...prev, index]
    );
  };

  const handleViewDocuments = (saleOrder: ISalesOrder) => {
    const legalEntityName = saleOrder?.legal_name || saleOrder?.legalEntity?.legal_name || '';
    const documents = saleOrder?.documents || [];

    setSelectedLegalEntityName(legalEntityName);
    setSelectedLegalEntityDocuments(documents);
    setShowDocumentsDialog(true);
  };

  const handleCloseDocumentsDialog = () => {
    setShowDocumentsDialog(false);
    setSelectedLegalEntityDocuments([]);
    setSelectedLegalEntityName('');
  };

  return (
    <FormProvider methods={methods} onSubmit={onSubmit}>
      <OrganizationInfoCard
        organizationData={organizationData}
        currentOrder={currentOrder}
        currentExistsOrder={currentExistsOrder}
        contacts={[]}
      />

      <Card sx={{ mb: 2 }}>
        <CardHeader title={t('Items list')} />

        <CardContent sx={{ px: 0, '&:last-child': { pb: 0 } }}>
          <Table stickyHeader aria-label="sticky table">
            <TableHead>
              <TableRow>
                <TableCell width={60} align="center">
                  {t('Item')}
                </TableCell>
                <TableCell>{t('Type')}</TableCell>
                <TableCell>{t('Product')}</TableCell>
                <TableCell>{t('SKU/Plan')}</TableCell>
                <TableCell>
                  <ListItemText primary={t('Domain')} secondary={t('Pre-sale')} />
                </TableCell>
                <TableCell align="center">{t('Provision Date')}</TableCell>
                <TableCell align="center">{t('Renewal Date')}</TableCell>
                <TableCell align="center">{t('Quantity')}</TableCell>

                <TableCell align="center" width={200}>
                  {t('Total Amount')}
                  <Tooltip
                    title={t(
                      `This amount represents the total of the contract; however, the amount to be billed may be lower. For more details, please expand this item and review all associated billing milestones.`
                    )}
                  >
                    <InfoIcon
                      sx={{ fontSize: 16, marginLeft: 0.5, verticalAlign: 'text-top' }}
                      color="error"
                    />
                  </Tooltip>
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {itemsCurrentOrder
                ?.sort((a: any, b: any) => a._id.localeCompare(b._id))
                .map((row: any, index: number) => {
                  const domain = currentExistsOrder
                    ? row.domain?.domain || null
                    : row.domain?.label || null;

                  const finalMilestones: IMilestoneItem[] =
                    [
                      row?.finalCustomMilestones,
                      row?.customMilestones,
                      row?.summary?.provision?.milestones,
                    ].find((m) => m?.length > 0) ?? [];

                  return (
                    <Fragment key={`${index}-${row._id}`}>
                      <TableRow key={`product-list-${index}-${row._id}`}>
                        <TableCell width={60} align="center">
                          {index + 1}
                        </TableCell>
                        <TableCell>
                          <Label
                            color={
                              row.orderItemType === ChoicesOrderItemType.newResource
                                ? 'primary'
                                : 'secondary'
                            }
                            variant="outlined"
                          >
                            {ChoicesOrderItemLabel[row.orderItemType]}
                          </Label>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle2">
                            {`${row?.product?.name || row?.product?.label}`}
                          </Typography>
                          <Chip
                            avatar={
                              <Avatar
                                alt={row?.partner?.name || row?.orderItem?.partner?.name}
                                src={decodeURI(
                                  row?.partner?.logo?.url ||
                                    row?.orderItem?.partner?.logo?.url ||
                                    ''
                                )}
                              />
                            }
                            variant="outlined"
                            size="small"
                            label={
                              row?.partner?.name ||
                              row?.orderItem?.partner?.name ||
                              row?.partner?.label ||
                              row?.orderItem?.partner?.label
                            }
                          />
                        </TableCell>

                        <TableCell>
                          <Stack>
                            <Typography variant="subtitle2">
                              {`${row?.sku?.name || row?.sku?.label}`}
                            </Typography>
                            <Typography variant="caption">
                              {`${row?.plan?.name || row?.plan?.label || ''}`}
                            </Typography>
                            <CopyText
                              text={row?.plan?.code || ''}
                              color="text.secondary"
                              variant="caption"
                              sx={{ fontSize: 'inherit' }}
                            />
                          </Stack>
                        </TableCell>

                        <TableCell>
                          <ListItemText
                            primary={
                              <ShowIf condition={!!domain} alternative="-">
                                <Iconify
                                  icon="ic:round-domain"
                                  sx={{ verticalAlign: 'text-bottom' }}
                                />{' '}
                                {domain}
                              </ShowIf>
                            }
                            secondary={
                              <ShowIf condition={!!row.pre_sale} alternative="-">
                                {' '}
                                <Tooltip title={t('Pre-sale')} arrow placement="top">
                                  <Chip
                                    avatar={
                                      <Avatar
                                        alt={row?.pre_sale?.firstName}
                                        src={decodeURI(row?.pre_sale?.avatar?.url || '')}
                                      />
                                    }
                                    variant="soft"
                                    color="secondary"
                                    size="small"
                                    label={`${row?.pre_sale?.firstName || ''} ${row?.pre_sale?.lastName || ''}`}
                                  />
                                </Tooltip>
                              </ShowIf>
                            }
                            primaryTypographyProps={{ fontWeight: '300' }}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {moment.isMoment(row?.summary?.provision?.provisionDate)
                            ? row.summary.provision.provisionDate.utc().format('L')
                            : moment(row?.summary?.provision?.provisionDate).utc().format('L')}
                        </TableCell>
                        <TableCell align="center">
                          {moment.isMoment(row?.summary?.provision?.endDate)
                            ? row.summary.provision.endDate.utc().format('L')
                            : moment(row?.summary?.provision?.endDate).utc().format('L')}
                        </TableCell>
                        <TableCell align="center">
                          <Label>{row.summary?.provision?.quantity}</Label>
                        </TableCell>

                        <TableCell align="center">
                          <RHFNumberText
                            value={row?.total}
                            decimalScale={2}
                            fixedDecimalScale
                            prefix="$ "
                          />
                          <IconButton onClick={() => toggleRow(index)}>
                            {expandedRows.includes(index) ? <ExpandLess /> : <ExpandMore />}
                          </IconButton>
                        </TableCell>
                      </TableRow>
                      <TableRow key={`product-prices-${index}-${row._id}`}>
                        <TableCell colSpan={9} style={{ padding: 0, border: 0 }}>
                          <Collapse in={expandedRows.includes(index)} timeout="auto" unmountOnExit>
                            <Table>
                              <TableHead>
                                <TableRow>
                                  <TableCell width="300px" align="center">
                                    {t('Billing Milestone')}
                                  </TableCell>
                                  <TableCell align="center">{t('Billing Date')}</TableCell>
                                  <TableCell width="200px" align="center">
                                    {t('Amount To Be Billed')}
                                  </TableCell>

                                  <TableCell>{}</TableCell>
                                </TableRow>
                              </TableHead>
                              <TableBody>
                                {finalMilestones
                                  .sort(
                                    (
                                      firstMilestone: IMilestoneItem,
                                      secondMilestone: IMilestoneItem
                                    ) =>
                                      moment(firstMilestone.paymentDate).unix() -
                                      moment(secondMilestone.paymentDate).unix()
                                  )
                                  .map((milestone: IMilestoneItem, milestoneIndex: number) => (
                                    <TableRow key={milestoneIndex}>
                                      <TableCell align="center">
                                        {`${t('Billing Milestone ')} ${milestoneIndex + 1}`}
                                      </TableCell>
                                      <TableCell align="center">
                                        {moment.isMoment(milestone?.paymentDate)
                                          ? milestone.paymentDate.utc().format('L')
                                          : moment(milestone?.paymentDate).utc().format('L')}
                                      </TableCell>
                                      <TableCell align="center">
                                        <RHFNumberText
                                          value={milestone?.total ?? milestone?.amount ?? 0}
                                          decimalScale={2}
                                          fixedDecimalScale
                                        />
                                      </TableCell>
                                      <TableCell>{}</TableCell>
                                    </TableRow>
                                  ))}
                              </TableBody>
                            </Table>
                          </Collapse>
                        </TableCell>
                      </TableRow>
                    </Fragment>
                  );
                })}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Card sx={{ mb: 2 }}>
        <CardHeader title={t('Billing distribution')} />
        <CardContent sx={{ px: 0, pb: 0, '&:last-child': { pb: 0 } }}>
          {dataSalesOrders && dataSalesOrders.length > 0 ? (
            <Box sx={{ width: '100%' }}>
              <TabContext value={value}>
                <Box sx={{ px: 3, borderBottom: 1, borderColor: 'divider' }}>
                  <TabList onChange={handleChange} aria-label="Legal Entity Tabs">
                    {dataSalesOrders?.map((saleOrder: ISalesOrder, idx: number) => (
                      <Tab
                        key={`tab-${saleOrder?._id}`}
                        label={saleOrder?.legal_name || saleOrder?.legalEntity?.legal_name}
                        value={`${idx}`}
                        wrapped
                      />
                    ))}
                  </TabList>
                </Box>
                {dataSalesOrders?.map((saleOrder: ISalesOrder, idx: number) => {
                  const totalByLegalEntity =
                    saleOrder?.totalAmount ??
                    (saleOrder?.salesOrdersItems || saleOrder?.items)?.reduce(
                      (acc: number, item: any) => acc + (item?.amount ?? 0),
                      0
                    ) ??
                    0;

                  return (
                    <TabPanel
                      key={`panel-${saleOrder?.legal_name || saleOrder?.legalEntity?.legal_name}`}
                      value={`${idx}`}
                      sx={{ px: 0 }}
                    >
                      <CardHeader
                        title={
                          <>
                            <Iconify
                              icon="solar:bill-bold-duotone"
                              width={20}
                              sx={{ verticalAlign: 'middle' }}
                            />{' '}
                            {saleOrder?.legal_name || saleOrder?.legalEntity?.legal_name}
                          </>
                        }
                      />
                      <CardContent sx={{ pt: 1 }}>
                        <Stack spacing={2} direction="row">
                          <Typography fontWeight="bold">{`${t('Tax number')} :`}</Typography>
                          <Typography>
                            {saleOrder?.tax_number || saleOrder?.legalEntity?.tax_number}
                          </Typography>
                        </Stack>

                        {saleOrder?.documents?.length > 0 && (
                          <Stack spacing={2} direction="row" alignItems="center">
                            <Typography
                              fontWeight="bold"
                              sx={{ minWidth: 140 }}
                            >{`${t('Attached Documents')} :`}</Typography>
                            <Button
                              variant="outlined"
                              size="small"
                              startIcon={<AttachFileIcon />}
                              onClick={() => handleViewDocuments(saleOrder)}
                              disabled={!saleOrder?.documents || saleOrder.documents.length === 0}
                            >
                              {t('View Documents')}{' '}
                              {saleOrder?.documents?.length > 0 &&
                                `(${saleOrder.documents.length})`}
                            </Button>
                          </Stack>
                        )}

                        <ShowIf condition={!!saleOrder?.OVNumber}>
                          <Stack spacing={2} direction="row">
                            <Typography fontWeight="bold">{`${t('Sales order number')} :`}</Typography>
                            <CopyText text={saleOrder?.OVNumber} />
                          </Stack>
                        </ShowIf>
                      </CardContent>
                      <CardContent sx={{ px: 0, '&:last-child': { pb: 0 } }}>
                        <Table stickyHeader aria-label="sticky table">
                          <TableHead>
                            <TableRow>
                              <TableCell>{t('Product')}</TableCell>
                              <TableCell>{t('Catalog name')}</TableCell>
                              <TableCell>{t('Type')}</TableCell>
                              <TableCell align="center">{t('Unit Amount')}</TableCell>
                              <TableCell>{t('Total Amount')}</TableCell>
                            </TableRow>
                          </TableHead>
                          <TableBody>
                            {(saleOrder?.salesOrdersItems || saleOrder?.items)
                              ?.sort((a: any, b: any) => {
                                const aId = String(a?.itemId || a?.orderItem || a?._id || '');
                                const bId = String(b?.itemId || b?.orderItem || b?._id || '');
                                return aId.localeCompare(bId);
                              })
                              .map((salesOrdersItems: any) => {
                                // Use salesOrdersItems directly if available, but search on itemsById
                                const item = salesOrdersItems?.product
                                  ? salesOrdersItems
                                  : itemsById[
                                      salesOrdersItems?.itemId || salesOrdersItems?.orderItem
                                    ];
                                return (
                                  <TableRow
                                    key={`${saleOrder?.legal_name || saleOrder?.legalEntity?.legal_name}-${salesOrdersItems.itemId || salesOrdersItems._id}`}
                                  >
                                    <TableCell>
                                      <Typography variant="subtitle2">
                                        {`${item?.product?.name || item?.product?.label} / ${item?.sku?.name || item?.sku?.label}`}
                                      </Typography>
                                      <Chip
                                        sx={{ mr: 1 }}
                                        avatar={
                                          <Avatar
                                            alt={
                                              item?.orderItem?.partner?.name || item?.partner?.name
                                            }
                                            src={decodeURI(
                                              item?.orderItem?.partner?.logo?.url ||
                                                item?.partner?.logo?.url ||
                                                ''
                                            )}
                                          />
                                        }
                                        variant="outlined"
                                        size="small"
                                        label={
                                          item?.orderItem?.partner?.name ||
                                          item?.partner?.name ||
                                          item?.partner?.label ||
                                          '-'
                                        }
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Stack>
                                        <Typography variant="caption">
                                          {`${item?.plan?.catalogName || item?.plan?.label || ''}`}
                                        </Typography>
                                        <CopyText
                                          color="text.secondary"
                                          variant="caption"
                                          text={item?.plan?.code || ''}
                                          sx={{ fontSize: 'inherit' }}
                                        />
                                      </Stack>
                                    </TableCell>
                                    <TableCell>
                                      <Label
                                        color={
                                          item?.orderItemType === ChoicesOrderItemType.newResource
                                            ? 'primary'
                                            : 'secondary'
                                        }
                                        variant="outlined"
                                      >
                                        {ChoicesOrderItemLabel[item?.orderItemType]}
                                      </Label>
                                    </TableCell>
                                    <TableCell align="center">
                                      {salesOrdersItems?.unitMeasure ===
                                        ChoicesOrderUnitMeasure.quantity && (
                                        <Label>{salesOrdersItems?.value ?? '0'}</Label>
                                      )}

                                      {salesOrdersItems?.unitMeasure ===
                                        ChoicesOrderUnitMeasure.percentageAmount && (
                                        <RHFNumberText
                                          value={salesOrdersItems?.value ?? 0}
                                          decimalScale={2}
                                          fixedDecimalScale
                                          suffix="%"
                                        />
                                      )}

                                      {salesOrdersItems?.unitMeasure ===
                                        ChoicesOrderUnitMeasure.amount && (
                                        <RHFNumberText
                                          value={salesOrdersItems?.value ?? 0}
                                          decimalScale={2}
                                          fixedDecimalScale
                                        />
                                      )}
                                    </TableCell>
                                    <TableCell>
                                      <RHFNumberText
                                        value={salesOrdersItems?.amount ?? 0}
                                        decimalScale={2}
                                        fixedDecimalScale
                                        prefix="$ "
                                      />
                                    </TableCell>
                                  </TableRow>
                                );
                              })}
                          </TableBody>
                        </Table>
                      </CardContent>
                      <Divider />
                      <CardActions sx={{ px: 3, pt: 2, pb: 0 }}>
                        <Grid container spacing={2} flexGrow={1}>
                          <Grid xs={7} md={9} sx={{ textAlign: 'right' }}>
                            <Typography variant="subtitle1">{t('Total')}:</Typography>
                          </Grid>
                          <Grid xs={5} md={3} sx={{ textAlign: 'right' }}>
                            <Typography variant="subtitle1" color="primary.main">
                              <RHFNumberText
                                value={totalByLegalEntity}
                                decimalScale={2}
                                fixedDecimalScale
                                prefix="$ "
                              />
                            </Typography>
                          </Grid>
                        </Grid>
                      </CardActions>
                    </TabPanel>
                  );
                })}
              </TabContext>
            </Box>
          ) : (
            <Box sx={{ px: 3, py: 2 }}>
              <Typography variant="body2" color="text.secondary" align="center">
                {t('No billing distribution to display')}
              </Typography>
            </Box>
          )}
        </CardContent>
      </Card>

      <Grid container justifyContent="flex-end" sx={{ mt: 2 }}>
        <Grid xs={4} sx={{ textAlign: 'left' }}>
          <ShowIf
            condition={!currentExistsOrder?.salesOrders[0]?.OVNumber && !validatorOrderCreated}
          >
            <LoadingButton
              color="primary"
              type="submit"
              onClick={onBack}
              variant="contained"
              sx={{ mr: 1 }}
            >
              {t('Back')}
            </LoadingButton>
          </ShowIf>
          <Button
            variant="outlined"
            sx={{ mr: 1 }}
            onClick={() => {
              onCancel();
            }}
          >
            {t('Cancel')}
          </Button>
        </Grid>
        <Grid xs={8} sx={{ textAlign: 'right' }}>
          <ShowIf
            condition={!currentExistsOrder?.salesOrders[0]?.OVNumber && !validatorOrderCreated}
          >
            <LoadingButton
              color="primary"
              type="submit"
              variant="contained"
              loading={isSubmitting || isLoading}
            >
              {t('Send')}
            </LoadingButton>
          </ShowIf>

          <ShowIf condition={currentOrder?.order?.status === ChoicesOrderStatus.provisioning}>
            <LoadingButton
              color="secondary"
              type="submit"
              onClick={() => setCompleteOrder(true)}
              sx={{ mr: 1 }}
              variant="contained"
              loading={isSubmitting || isLoading}
            >
              {t('Complete Order')}
            </LoadingButton>
          </ShowIf>
        </Grid>
      </Grid>

      <Dialog
        open={showDocumentsDialog}
        onClose={handleCloseDocumentsDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Typography variant="h6">{t('Attached Documents')}</Typography>
            <IconButton onClick={handleCloseDocumentsDialog}>
              <Iconify icon="mingcute:close-line" />
            </IconButton>
          </Stack>
          <Typography variant="body2" color="text.secondary">
            {t('View documents related to this billing order.')} - {selectedLegalEntityName}
          </Typography>
        </DialogTitle>
        <DialogContent>
          <OrderDocumentsSection
            documents={selectedLegalEntityDocuments}
            title={selectedLegalEntityName}
            sectionsToHide={{ AddButton: true, EditButton: true, DeleteButton: true }}
          />
        </DialogContent>
      </Dialog>
    </FormProvider>
  );
}
